import {
  imperativeServerUserGoogleSocialSessionMutation,
  imperativeServerUserFacebookSocialSessionMutation,
  imperativeServerUserSessionMutation,
  imperativeServerUserAppleSocialSessionMutation,
} from '@utils/imperative-node-mutations';
import { imperativeServerUserRefreshSessionMutation } from '@utils/imperative-node-mutations/imperative-node-user-refresh-session-mutation';
import NextAuth, { NextAuthConfig } from 'next-auth';
import * as _JWT from 'next-auth/jwt';

import Credentials from 'next-auth/providers/credentials';
import Google from 'next-auth/providers/google';
import FacebookProvider from 'next-auth/providers/facebook';
// import AppleProvider from 'next-auth/providers/apple';
import { imperativeServerCurrentUserQuery } from '@utils/imperative-node-queries';
import { UserSessionFieldsFragment } from '@gql';

// Extends NextAuth's User type with our custom fields for user data.
// Used server-side only, contains sensitive data like access/refresh tokens.
// Returned by the authorize function.
declare module 'next-auth' {
  interface User {
    id?: string;
    email?: string | null;
    avatarUrl?: string;
    firstName?: string;
    lastName?: string;
    isHost: boolean;
    accessToken?: string;
    refreshToken?: string;
    registeredAt?: string;
    lastAuthenticatedAt?: string;
  }
}

// Extends NextAuth's JWT type with our custom fields for token storage.
// Used server-side only (HTTP only cookie), contains sensitive data like access/refresh tokens.
// Returned by the jwt callback.
declare module 'next-auth/jwt' {
  interface JWT {
    id?: string;
    email?: string | null;
    avatarUrl?: string;
    firstName?: string;
    lastName?: string;
    isHost: boolean;
    accessToken?: string;
    refreshToken?: string;
    isFirstSession?: boolean;
  }
}

// Extends NextAuth's Session type with our custom fields for session data.
// Used client-side only (via useSession hook), contains only safe-to-expose data.
declare module 'next-auth' {
  interface Session {
    user: {
      id: string;
      email?: string;
      avatarUrl?: string;
      firstName?: string;
      lastName?: string;
      isHost: boolean;
      accessToken?: string;
    };
    isFirstSession?: boolean;
  }
}

export const authConfig = {
  pages: {
    signIn: '/login',
  },
  providers: [
    Credentials({
      id: 'EngineRefreshTokenLogin',
      name: 'Engine Refresh Token Login',
      credentials: {
        refresh_token: { label: 'RefreshToken', type: 'password' },
      },
      // Validates refresh token and creates/updates user session.
      // Called during sign in flow to authenticate user with refresh token.
      // Returns user object that will be stored in the JWT token.
      async authorize(credentials) {
        const user = await getAuthUserFromRefreshToken(
          credentials.refresh_token as string
        );
        // console.log('engine refresh token authorize callback, user:', user);
        return user;
      },
    }),
    Credentials({
      id: 'AppleToken',
      name: 'Apple Token',
      credentials: {
        token: { label: 'Token', type: 'password' },
        email: { label: 'Email', type: 'text' },
        firstName: { label: 'First Name', type: 'text' },
        lastName: { label: 'Last Name', type: 'text' },
      },
      // Validates Apple Sign In credentials and creates/updates user session.
      // Called during sign in flow to authenticate user with Apple token.
      // Returns user object that will be stored in the JWT token.
      async authorize(credentials) {
        let token = credentials.token as string;
        const input: {
          accessToken: string;
          email?: string;
          firstName?: string;
          lastName?: string;
        } = {
          accessToken: token,
        };

        if (credentials.email == null || credentials.email !== '') {
          input.email = credentials.email as string;
        }

        if (credentials.firstName == null || credentials.firstName !== '') {
          input.firstName = credentials.firstName as string;
        }

        if (credentials.lastName == null || credentials.lastName !== '') {
          input.lastName = credentials.lastName as string;
        }

        const userQuery =
          await imperativeServerUserAppleSocialSessionMutation(input);

        if (userQuery.data?.userAppleSocialSession.__typename !== 'User') {
          throw new Error('User not found.');
        }

        const user = toAuthUser(userQuery.data.userAppleSocialSession);
        // console.log('apple authorize callback, user:', user);
        return user;
      },
    }),
    Credentials({
      credentials: {
        email: { label: 'Username', type: 'text' },
        password: { label: 'Password', type: 'password' },
      },
      // Validates user credentials and creates/updates user session.
      // Called during sign in flow to authenticate user with email/password.
      // Returns user object that will be stored in the JWT token.
      async authorize(credentials) {
        let email = credentials.email as string;
        let password = credentials.password as string;

        const userQuery = await imperativeServerUserSessionMutation({
          email: email,
          password: password,
        });

        if (userQuery.data?.userSession.__typename !== 'User') {
          throw new Error('User not found.');
        }
        const user = toAuthUser(userQuery.data.userSession);

        // console.log('credentials authorize callback, user:', user);
        return user;
      },
    }),
    Google({
      clientId: process.env.AUTH_GOOGLE_ID,
      clientSecret: process.env.AUTH_GOOGLE_SECRET,
    }),
    FacebookProvider({
      clientId: process.env.AUTH_FACEBOOK_ID,
      clientSecret: process.env.AUTH_FACEBOOK_SECRET,
    }),
    // AppleProvider is not implemented yet
    // Waiting on https://github.com/nextauthjs/next-auth/issues/6788#issuecomment-2156123665
    // AppleProvider({
    //   clientId: process.env.AUTH_APPLE_ID,
    //   clientSecret: process.env.AUTH_APPLE_SECRET || '',
    //   wellKnown: 'https://appleid.apple.com/.well-known/openid-configuration',
    //   checks: ['pkce'],
    //   token: {
    //     url: `https://appleid.apple.com/auth/token`,
    //   },
    //   authorization: {
    //     url: 'https://appleid.apple.com/auth/authorize',
    //     params: {
    //       scope: '',
    //       response_type: 'code',
    //       response_mode: 'form_post',
    //       state: crypto.randomUUID(),
    //     },
    //   },
    //   client: {
    //     token_endpoint_auth_method: 'client_secret_post',
    //   },
    // }),
  ],
  callbacks: {
    /**
     * Manages JWT token lifecycle and updates.
     * Called on sign up, sign in, and update.
     * Handles token expiration and refresh for all auth providers.
     * Returns token object that will be stored in HTTP-only cookie.
     */
    async jwt({ token, user, account, trigger }) {
      const existingAuthUser = user;
      let freshAuthUser = null;

      if (trigger === 'update') {
        // Called when an update is explicitly called from the client
        // This looks like update({ ...session?.user });
        freshAuthUser = await getAuthUserFromRefreshToken(token.refreshToken!);
      } else if (trigger === 'signIn') {
        // Called when user is initially authenticated

        // This will provider a provider on the account object
        // in the event of external providers like google or facebook we need
        // to make a call to the server to get the user to validate that
        // able to sign in with that provider.
        if (account?.provider === 'google') {
          const userQuery =
            await imperativeServerUserGoogleSocialSessionMutation({
              accessToken: account.id_token!,
            });

          if (userQuery.data?.userGoogleSocialSession.__typename !== 'User') {
            throw new Error('User not found.');
          }
          freshAuthUser = toAuthUser(userQuery.data.userGoogleSocialSession);
        } else if (account?.provider === 'facebook') {
          const userQuery =
            await imperativeServerUserFacebookSocialSessionMutation({
              accessToken: account.access_token!,
            });

          if (userQuery.data?.userFacebookSocialSession.__typename !== 'User') {
            throw new Error('User not found.');
          }
          freshAuthUser = toAuthUser(userQuery.data.userFacebookSocialSession);
        } else {
          // if the user is not a google or facebook user (e.g. apple or email/password)
          // use the user object that was passed in from credentials
          freshAuthUser = existingAuthUser;
        }
      } else {
        // Sign up or empty trigger (loading of JWT)
        // In the event that the token is being fetched we will check if the token
        // has expired and if so we will refresh the token. Else we will return the
        // token as is.

        const decodedToken = decodeJwt(token.accessToken!);

        if (Date.now() < decodedToken.exp * 1000) {
          // token is still valid, no need to refresh
          return token;
        } else {
          // token is expired, refresh the token
          freshAuthUser = await getAuthUserFromRefreshToken(
            token.refreshToken!
          );
        }
      }

      // merge the updated user with the token

      token.id = freshAuthUser.id;
      token.accessToken = freshAuthUser.accessToken;
      token.avatarUrl = freshAuthUser.avatarUrl;
      token.email = freshAuthUser.email;
      token.firstName = freshAuthUser.firstName;
      token.lastName = freshAuthUser.lastName;
      token.isHost = freshAuthUser.isHost;
      token.refreshToken = freshAuthUser.refreshToken;
      token.registeredAt = freshAuthUser.registeredAt;
      token.lastAuthenticatedAt = freshAuthUser.lastAuthenticatedAt;

      // console.log('jwt callback, token:', token);

      return token;
    },

    // Creates client-side session from JWT token.
    // Called after jwt callback to expose safe user data to client.
    // Filters sensitive data (like refreshToken) from token before sending to client.
    // Returns session object available via useSession() hook.
    async session({ session, token }) {
      session.user.id = token.id!;
      session.user.avatarUrl = token.avatarUrl;
      session.user.email = token.email!;
      session.user.firstName = token.firstName;
      session.user.lastName = token.lastName;
      session.user.isHost = token.isHost;
      session.user.accessToken = token.accessToken;
      session.isFirstSession = token.registeredAt === token.lastAuthenticatedAt;

      return session;
    },
  },
  cookies: {
    pkceCodeVerifier: {
      name: 'next-auth.pkce.code_verifier',
      options: {
        httpOnly: true,
        sameSite: 'none',
        path: '/',
        secure: true,
      },
    },
  },
  debug: process.env.NODE_ENV !== 'production' ? true : false,
  session: {
    strategy: 'jwt',
    maxAge: 2592000, // 30 days
  },
} satisfies NextAuthConfig;

export const { handlers, auth, signIn, signOut } = NextAuth(authConfig);

async function getAuthUserFromRefreshToken(refreshToken: string) {
  // Will notice an error in Local development when reactStrictMode == true
  // your components will re-render an extra time to find bugs caused by impure rendering
  // when reactStrictMode == false, the components will not re-render an extra time
  const userQuery = await imperativeServerUserRefreshSessionMutation({
    refreshToken: refreshToken,
  });

  if (userQuery.data?.userSessionRefresh.__typename !== 'User') {
    throw new Error('User not found during refresh.');
  }

  const currentUserQuery = await imperativeServerCurrentUserQuery(
    userQuery.data.userSessionRefresh.accessToken!
  );

  if (currentUserQuery.data.currentUser.__typename !== 'User') {
    throw new Error('User not found.');
  }

  // return user object.
  return toAuthUser(userQuery.data.userSessionRefresh);
}

// async function getUserFromAccessToken(accessToken: string) {
//   const currentUserQuery = await imperativeServerCurrentUserQuery(accessToken);

//   if (currentUserQuery.data.currentUser.__typename !== 'User') {
//     throw new Error('User not found.');
//   }

//   return toAuthUser(currentUserQuery.data.currentUser);
// }

function toAuthUser(engineUser: UserSessionFieldsFragment) {
  return {
    id: engineUser.id,
    email: engineUser.email,
    avatarUrl: engineUser.avatarUrl,
    firstName: engineUser.firstName,
    lastName: engineUser.lastName,
    isHost: engineUser.accountRole ? true : false,
    accessToken: engineUser.accessToken!,
    refreshToken: engineUser.refreshToken!,
    registeredAt: engineUser.registeredAt,
    lastAuthenticatedAt: engineUser.lastAuthenticatedAt,
  };
}

type EngineJWT = {
  aud: string;
  // expires at seconds
  exp: number;
  // issued at seconds
  iat: number;
  // issuer
  iss: string;
  // jwt id
  jti: string;
  // not before
  nbf: number;
  // subject
  sub: string;
  // type
  typ: string;
  // user session id
  user_session_id: string;
  // version
  version: number;
};

function decodeJwt(token: string): EngineJWT {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const payload = JSON.parse(Buffer.from(base64, 'base64').toString());
  return payload as EngineJWT;
}

// <!-- Apple gen commands for apple auth secret. Expires every 6 months. -->
// chmod +x scripts/apple-gen-secret.mjs
// export APPLE_KEY_ID="65BU674Q9T"
// export APPLE_TEAM_ID="Y663485639"
// export APPLE_PRIVATE_KEY="-----BEGIN PRIVATE KEY-----\nMIGTAgEAMBMGByqGSM49AgEGCCqGSM49AwEHBHkwdwIBAQQg5peozOaYgh/nt7S5\ne/vOsIUUkTLNv041cMrzLJwG9VygCgYIKoZIzj0DAQehRANCAAT97lgo6yAs/870\nKj8BT2oV8dfxObXspfe8/CKgMTegVBKYnDiLYS1l4kYTKMRFEelzZtAiGLg7EUWY\n7cD0wSee\n-----END PRIVATE KEY-----"
// export APPLE_CLIENT_ID="com.bnbf-dev.auth.client"

// ./scripts/apple-gen-secret.mjs --kid $APPLE_KEY_ID --iss $APPLE_TEAM_ID --private_key $APPLE_PRIVATE_KEY --client $APPLE_CLIENT_ID

// <!-- Currently a work in progress for apple in next auth -->
// https://github.com/nextauthjs/next-auth/issues/6788#issuecomment-2156123665
